import React, { useRef, useEffect, useState, useCallback } from 'react';
import './SideBySide.css';

const SideBySide = ({ path1, path2 }) => {
  const image1Ref = useRef(null);
  const image2Ref = useRef(null);

  const cursor1Ref = useRef(null);
  const cursor2Ref = useRef(null);

  const [isHovering, setIsHovering] = useState(false);

  // Mode local : 'cross' | 'horizontal' | 'twoPoints'
  const [drawMode, setDrawMode] = useState('cross');

  const [imageSize, setImageSize] = useState({ height: '100vh', width: 'auto' });

  const [pos1, setPos1] = useState({ x: 0, y: 0 });
  const [pos2, setPos2] = useState({ x: 0, y: 0 });

  // Pour le mode "twoPoints", on stocke deux points (p1, p2) sur chaque image
  const [linePoints, setLinePoints] = useState(null);

  const [lastClickTime, setLastClickTime] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);

  const [lines, setLines] = useState([]);

  // Fonction utilitaire pour calculer les coordonnées
  const calculateCoordinates = (event, ref, otherRef) => {
    const rect = ref.current.getBoundingClientRect();
    const otherRect = otherRef.current.getBoundingClientRect();

    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    const ratioX = offsetX / rect.width;
    const ratioY = offsetY / rect.height;

    return {
      here: { x: offsetX, y: offsetY },
      other: {
        x: ratioX * otherRect.width,
        y: ratioY * otherRect.height
      }
    };
  };

  useEffect(() => {
    const updateSize = () => {
      let height = window.innerHeight;
      height = height * 0.6; 
      setImageSize({ height: `${height}px`, width: 'auto' });
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const handleMouseMove = useCallback((event, imageRef, setPos, otherSetPos, otherRef) => {
    if (!imageRef.current || !otherRef.current) return;

    const coords = calculateCoordinates(event, imageRef, otherRef);
    setPos(coords.here);
    otherSetPos(coords.other);
  }, []);

  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const handleClick = useCallback((event, whichImage) => {
    if (drawMode !== 'twoPoints') return;

    const currentTime = Date.now();
    if (currentTime - lastClickTime < 300) return;
    setLastClickTime(currentTime);

    const ref = whichImage === 1 ? image1Ref : image2Ref;
    const otherRef = whichImage === 1 ? image2Ref : image1Ref;

    if (!ref.current || !otherRef.current) return;

    const coords = calculateCoordinates(event, ref, otherRef);
    const point = whichImage === 1 
      ? { i1: coords.here, i2: coords.other }
      : { i2: coords.here, i1: coords.other };

    if (!linePoints || linePoints.p2) {
      setIsDrawing(true);
      setLinePoints({ p1: point, p2: null });
    } else {
      const newLine = { p1: linePoints.p1, p2: point };
      setLines(prev => [...prev.slice(-4), newLine]);
      setIsDrawing(false);
      setLinePoints(null);
    }
  }, [drawMode, linePoints, lastClickTime]);

  useEffect(() => {
    const img1 = image1Ref.current;
    const img2 = image2Ref.current;

    const onImage1Move = (e) => handleMouseMove(e, image1Ref, setPos1, setPos2, image2Ref);
    const onImage2Move = (e) => handleMouseMove(e, image2Ref, setPos2, setPos1, image1Ref);
    const onClick1 = (e) => handleClick(e, 1);
    const onClick2 = (e) => handleClick(e, 2);

    if (img1) {
      img1.addEventListener('mousemove', onImage1Move);
      img1.addEventListener('mouseenter', handleMouseEnter);
      img1.addEventListener('mouseleave', handleMouseLeave);
      img1.addEventListener('click', onClick1);
    }
    if (img2) {
      img2.addEventListener('mousemove', onImage2Move);
      img2.addEventListener('mouseenter', handleMouseEnter);
      img2.addEventListener('mouseleave', handleMouseLeave);
      img2.addEventListener('click', onClick2);
    }

    return () => {
      if (img1) {
        img1.removeEventListener('mousemove', onImage1Move);
        img1.removeEventListener('mouseenter', handleMouseEnter);
        img1.removeEventListener('mouseleave', handleMouseLeave);
        img1.removeEventListener('click', onClick1);
      }
      if (img2) {
        img2.removeEventListener('mousemove', onImage2Move);
        img2.removeEventListener('mouseenter', handleMouseEnter);
        img2.removeEventListener('mouseleave', handleMouseLeave);
        img2.removeEventListener('click', onClick2);
      }
    };
  }, [drawMode, handleMouseMove, handleClick]);

  const renderLines = useCallback((whichImage) => {
    return (
      <svg className="overlay-svg">
        {/* Lignes existantes */}
        {lines.map((line, index) => (
          <line
            key={`line-${index}`}
            x1={line.p1[`i${whichImage}`]?.x}
            y1={line.p1[`i${whichImage}`]?.y}
            x2={line.p2[`i${whichImage}`]?.x}
            y2={line.p2[`i${whichImage}`]?.y}
            stroke="red"
            strokeWidth="2"
          />
        ))}
        {/* Ligne en cours */}
        {linePoints?.p1 && drawMode === 'twoPoints' && (
          <line
            x1={linePoints.p1[`i${whichImage}`]?.x}
            y1={linePoints.p1[`i${whichImage}`]?.y}
            x2={whichImage === 1 ? pos1.x : pos2.x}
            y2={whichImage === 1 ? pos1.y : pos2.y}
            stroke="red"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
        )}
      </svg>
    );
  }, [lines, linePoints, drawMode, pos1, pos2]);

  const resetButton = (
    <button 
      onClick={() => {
        setLines([]);
        setLinePoints(null);
        setIsDrawing(false);
      }}
      disabled={lines.length === 0 && !linePoints}
      className="reset-button"
    >
      Reset Lines ({lines.length}/5)
    </button>
  );

  // Effet combiné pour la gestion des changements de mode et d'images
  useEffect(() => {
    setLinePoints(null);
    setIsDrawing(false);
    setLines([]); // Reset des lignes au changement de mode/image
  }, [drawMode, path1, path2]);

  return (
    <div className="side-by-side-container d-flex justify-content-center">
      {/* Petit sous-menu local pour choisir cross/horizontal/2points */}
      <div className="sub-menu" id="subMenu" style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)' }}>
        <button onClick={() => setDrawMode('cross')} disabled={drawMode === 'cross'}>
          Cross
        </button>
        <button onClick={() => setDrawMode('horizontal')} disabled={drawMode === 'horizontal'}>
          Horizontal
        </button>
        <button onClick={() => setDrawMode('twoPoints')} disabled={drawMode === 'twoPoints'}>
          Two Points
        </button>

        {drawMode === 'twoPoints' && (
          <div>
            {resetButton}
          </div>
        )}
      </div>

      {/* IMAGE 1 */}
      <div className="position-relative image-container">
        <img 
          ref={image1Ref} 
          src={path1} 
          alt="Image 1" 
          className="img-fluid side-by-side-image" 
          style={imageSize}
        />
        {isHovering && drawMode === 'cross' && (
          <div 
            ref={cursor1Ref} 
            className="custom-cursor" 
            style={{
              left: `${pos1.x}px`,
              top: `${pos1.y}px`,
            }}
          />
        )}

        {isHovering && drawMode === 'horizontal' && (
          <div 
            className="horizontal-line" 
            style={{
              top: `${pos1.y}px`,
              width: '100%',
            }}
          />
        )}

        {drawMode === 'twoPoints' && renderLines(1)}
      </div>

      {/* IMAGE 2 */}
      <div className="position-relative image-container">
        <img 
          ref={image2Ref} 
          src={path2} 
          alt="Image 2" 
          className="img-fluid side-by-side-image" 
          style={imageSize}
        />
        {isHovering && drawMode === 'cross' && (
          <div 
            ref={cursor2Ref} 
            className="custom-cursor" 
            style={{
              left: `${pos2.x}px`,
              top: `${pos2.y}px`,
            }}
          />
        )}

        {isHovering && drawMode === 'horizontal' && (
          <div 
            className="horizontal-line"
            style={{
              top: `${pos2.y}px`,
              width: '100%',
            }}
          />
        )}

        {drawMode === 'twoPoints' && renderLines(2)}
      </div>
    </div>
  );
};

export default SideBySide;